import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function ChangePassword({ isCompanyUser = false }) {
  const [showForm, setShowForm] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (!currentPassword) { toast.error("Digite a senha atual"); return; }
    if (!newPassword || newPassword.length < 6) { toast.error("Nova senha mínimo 6 caracteres"); return; }
    if (newPassword !== confirmPassword) { toast.error("Senhas não coincidem"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Senha alterada com sucesso!");
      setShowForm(false);
      setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
    setSaving(false);
  };

  return (
    <Card className="border-white/5">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2"><Lock className="w-4 h-4 text-orange-400" /> Alterar Senha</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!showForm ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Altere sua senha de acesso</p>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setShowForm(true)} data-testid="change-password-btn"
                className="bg-orange-600 hover:bg-orange-700 text-white text-xs">
                Alterar Senha
              </Button>
              {isCompanyUser && (
                <Button size="sm" variant="outline" onClick={() => navigate("/reset-password")} data-testid="forgot-password-btn"
                  className="border-white/10 text-xs">
                  Esqueci a senha
                </Button>
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3 animate-fade-in">
            <div className="space-y-1.5">
              <Label className="text-xs">Senha atual</Label>
              <div className="relative">
                <Input data-testid="current-password-input" type={showCurrent ? "text" : "password"}
                  value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
                  className="h-9 bg-black/20 border-white/10 pr-9" />
                <button type="button" onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showCurrent ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Nova senha</Label>
              <div className="relative">
                <Input data-testid="new-password-input" type={showNew ? "text" : "password"}
                  value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres" className="h-9 bg-black/20 border-white/10 pr-9" />
                <button type="button" onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showNew ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Confirmar nova senha</Label>
              <Input data-testid="confirm-new-password-input" type="password"
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                className="h-9 bg-black/20 border-white/10" />
              {newPassword && confirmPassword && newPassword !== confirmPassword && (
                <p className="text-xs text-red-400">Senhas não coincidem</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSubmit} disabled={saving} data-testid="save-new-password-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white text-xs">
                {saving ? <div className="loading-spinner" /> : "Salvar"}
              </Button>
              <Button size="sm" variant="ghost" onClick={() => { setShowForm(false); setCurrentPassword(""); setNewPassword(""); setConfirmPassword(""); }}
                className="text-xs">Cancelar</Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
